import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Collapse,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { AutoAwesome, Refresh, Close } from '@mui/icons-material';
import { aiBuilderAPI } from '../../services/api';
import LoadingModal from '../LoadingModal';

interface ContextOption {
  label: string;
  definition: string | null;
}

interface ContextQuestion {
  question: string;
  options: ContextOption[];
}

interface ContextSuggestions {
  questions: ContextQuestion[];
}

interface ContextAssistantProps {
  topic: string;
  industry: string;
  currentContext: string;
  onContextUpdate: (newContext: string) => void;
  disabled?: boolean;
  showButton?: boolean;
  showPanel?: boolean;
  sharedState?: {
    isLoadingSuggestions: boolean;
    setIsLoadingSuggestions: (v: boolean) => void;
    isFormattingContext: boolean;
    setIsFormattingContext: (v: boolean) => void;
    suggestionsOpen: boolean;
    setSuggestionsOpen: (v: boolean) => void;
    suggestions: ContextSuggestions | null;
    setSuggestions: (v: ContextSuggestions | null) => void;
    selectedAnswers: SelectedAnswers;
    setSelectedAnswers: (v: SelectedAnswers | ((prev: SelectedAnswers) => SelectedAnswers)) => void;
    error: string | null;
    setError: (v: string | null) => void;
  };
}

interface SelectedAnswers {
  [key: number]: string[];
}

const ContextAssistant: React.FC<ContextAssistantProps> = ({
  topic,
  industry,
  currentContext,
  onContextUpdate,
  disabled = false,
  showButton = true,
  showPanel = true,
  sharedState,
}) => {
  // Use shared state if provided, otherwise use local state
  const [localIsLoadingSuggestions, setLocalIsLoadingSuggestions] = useState(false);
  const [localIsFormattingContext, setLocalIsFormattingContext] = useState(false);
  const [localSuggestionsOpen, setLocalSuggestionsOpen] = useState(false);
  const [localSuggestions, setLocalSuggestions] = useState<ContextSuggestions | null>(null);
  const [localSelectedAnswers, setLocalSelectedAnswers] = useState<SelectedAnswers>({});
  const [localError, setLocalError] = useState<string | null>(null);
  
  // State for tracking visible definitions
  const [visibleDefinitions, setVisibleDefinitions] = useState<string[]>([]);
  
  const isLoadingSuggestions = sharedState?.isLoadingSuggestions ?? localIsLoadingSuggestions;
  const setIsLoadingSuggestions = sharedState?.setIsLoadingSuggestions ?? setLocalIsLoadingSuggestions;
  const isFormattingContext = sharedState?.isFormattingContext ?? localIsFormattingContext;
  const setIsFormattingContext = sharedState?.setIsFormattingContext ?? setLocalIsFormattingContext;
  const suggestionsOpen = sharedState?.suggestionsOpen ?? localSuggestionsOpen;
  const setSuggestionsOpen = sharedState?.setSuggestionsOpen ?? setLocalSuggestionsOpen;
  const suggestions = sharedState?.suggestions ?? localSuggestions;
  const setSuggestions = sharedState?.setSuggestions ?? setLocalSuggestions;
  const selectedAnswers = sharedState?.selectedAnswers ?? localSelectedAnswers;
  const setSelectedAnswers = sharedState?.setSelectedAnswers ?? setLocalSelectedAnswers;
  const error = sharedState?.error ?? localError;
  const setError = sharedState?.setError ?? setLocalError;

  // Fetch context suggestions from backend
  const handleGetSuggestions = async () => {
    if (!topic.trim()) {
      setError('Please enter an audit topic first');
      return;
    }

    try {
      setIsLoadingSuggestions(true);
      setError(null);
      
      console.log('🤖 Fetching context suggestions...');
      
      const data = await aiBuilderAPI.getContextSuggestions({
        topic,
        industry: industry || undefined,
        current_context: currentContext || undefined,
      });
      
      if (data.success) {
        console.log('✅ Suggestions loaded', data.suggestions);
        setSuggestions(data.suggestions);
        setSelectedAnswers({});
        setVisibleDefinitions([]);
        setLastClickedQuestion(null);
        setSuggestionsOpen(true);
      } else {
        throw new Error(data.error || 'Failed to fetch suggestions');
      }
    } catch (error: any) {
      console.error('❌ Error fetching suggestions:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch suggestions. Please try again.');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Track the last question that was clicked
  const [lastClickedQuestion, setLastClickedQuestion] = useState<number | null>(null);
  
  // Handle option selection - supports multiple selections per question
  const handleOptionClick = (questionIndex: number, optionLabel: string) => {
    // Clear all definitions when switching to a different question
    if (lastClickedQuestion !== null && lastClickedQuestion !== questionIndex) {
      setVisibleDefinitions([]);
    }
    setLastClickedQuestion(questionIndex);
    
    // Check if option is currently selected before updating
    const currentSelections = selectedAnswers[questionIndex] || [];
    const isCurrentlySelected = currentSelections.includes(optionLabel);
    
    // Update selected answers
    setSelectedAnswers(prev => {
      const currentSelections = prev[questionIndex] || [];
      const isSelected = currentSelections.includes(optionLabel);
      
      return {
        ...prev,
        [questionIndex]: isSelected
          ? currentSelections.filter(o => o !== optionLabel) // Remove if already selected
          : [...currentSelections, optionLabel] // Add if not selected
      };
    });
    
    // Find the option object to check if it has a definition
    const question = suggestions?.questions[questionIndex];
    const optionObj = question?.options.find(opt => opt.label === optionLabel);
    
    // Show definition if option has one and it's being selected (not deselected)
    if (!isCurrentlySelected && optionObj?.definition) {
      setVisibleDefinitions(prev => {
        if (!prev.includes(optionLabel)) {
          return [...prev, optionLabel];
        }
        return prev;
      });
    } else if (isCurrentlySelected) {
      // Hide definition if option is being deselected
      setVisibleDefinitions(prev => prev.filter(def => def !== optionLabel));
    }
  };
  
  // Close a specific definition
  const handleCloseDefinition = (option: string) => {
    setVisibleDefinitions(prev => prev.filter(def => def !== option));
  };

  // Format context with LLM
  const handleApplyAnswers = async () => {
    // Build answers array - handle multiple selections
    const answersArray = suggestions?.questions.map((q, idx) => {
      const answers = selectedAnswers[idx] || [];
      return {
        question: q.question,
        answer: answers.length > 0 ? answers.join(', ') : 'Not specified'
      };
    }) || [];

    try {
      setIsFormattingContext(true);
      setError(null);
      
      console.log('🤖 Formatting context with answers...');
      
      const data = await aiBuilderAPI.formatContext({
        topic,
        industry: industry || undefined,
        original_context: currentContext || undefined,
        answers: answersArray,
      });
      
      if (data.success) {
        console.log('✅ Context formatted successfully');
        onContextUpdate(data.formatted_context);
        setSuggestionsOpen(false);
        setSuggestions(null);
        setSelectedAnswers({});
        setVisibleDefinitions([]);
        setLastClickedQuestion(null);
      } else {
        throw new Error(data.error || 'Failed to format context');
      }
    } catch (error: any) {
      console.error('❌ Error formatting context:', error);
      setError(error.response?.data?.error || error.message || 'Failed to format context. Please try again.');
    } finally {
      setIsFormattingContext(false);
    }
  };

  // Regenerate suggestions
  const handleRegenerate = () => {
    handleGetSuggestions();
  };

  // Dismiss suggestions
  const handleDismiss = () => {
    setSuggestionsOpen(false);
    setSuggestions(null);
    setSelectedAnswers({});
    setVisibleDefinitions([]);
    setLastClickedQuestion(null);
    setError(null);
  };

  return (
    <Box>
      {/* Loading Modals */}
      <LoadingModal
        open={isLoadingSuggestions}
        title="Getting Suggestions"
        message="AI is analyzing your topic to generate helpful context suggestions."
      />
      
      <LoadingModal
        open={isFormattingContext}
        title="Formatting Context"
        message="AI is processing your answers and formatting them into a clear description."
      />
      
      {/* Get Suggestions Link - Subtle Style */}
      {showButton && !suggestionsOpen && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <AutoAwesome sx={{ fontSize: 16, color: 'primary.main' }} />
          <Button
            variant="text"
            onClick={handleGetSuggestions}
            disabled={disabled || isLoadingSuggestions || !topic.trim()}
            size="small"
            sx={{ 
              textTransform: 'none',
              minWidth: 'auto',
              p: 0,
              fontSize: '0.875rem',
              fontWeight: 400,
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            {isLoadingSuggestions ? 'Loading...' : 'Get suggestions'}
          </Button>
        </Box>
      )}

      {/* Error Alert */}
      {showPanel && error && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Suggestions Panel */}
      {showPanel && <Collapse in={suggestionsOpen}>
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            mt: 1, 
            bgcolor: (theme) => 
              theme.palette.mode === 'dark' 
                ? 'rgba(33, 150, 243, 0.08)' // Subtle blue tint in dark mode
                : 'rgba(33, 150, 243, 0.04)', // Very subtle blue tint in light mode
            borderColor: 'primary.main',
            borderWidth: 2,
            borderStyle: 'solid'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome color="primary" fontSize="small" />
              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Suggestions to improve your description
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={handleRegenerate} disabled={isFormattingContext}>
                <Refresh fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleDismiss} disabled={isFormattingContext}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Guidance Text */}
          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            To improve the audit context, answer the questions below by selecting relevant options. 
            Click "Apply Answers to Description" to generate a well-formatted paragraph.
          </Typography>

          <Typography variant="caption" sx={{ mb: 2, display: 'block', color: 'text.secondary' }}>
            Consider answering these questions:
          </Typography>

          {/* Questions and Options */}
          {suggestions?.questions.map((question, qIdx) => (
            <Box key={qIdx} sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                {question.question}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {question.options.map((option, oIdx) => {
                  const isSelected = (selectedAnswers[qIdx] || []).includes(option.label);
                  return (
                    <Chip
                      key={oIdx}
                      label={option.label}
                      onClick={() => handleOptionClick(qIdx, option.label)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: isSelected 
                          ? (theme) => theme.palette.primary.main
                          : (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.08)' 
                            : 'background.paper',
                        color: isSelected 
                          ? (theme) => theme.palette.primary.contrastText
                          : 'text.primary',
                        borderColor: isSelected 
                          ? (theme) => theme.palette.primary.main
                          : (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.23)' 
                            : 'divider',
                        borderWidth: 1.5,
                        fontWeight: isSelected ? 600 : 400,
                        '&:hover': {
                          bgcolor: isSelected 
                            ? (theme) => theme.palette.primary.dark
                            : (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.16)'
                              : 'action.hover',
                          borderColor: isSelected
                            ? (theme) => theme.palette.primary.dark
                            : (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.4)'
                              : 'primary.main'
                        }
                      }}
                      disabled={isFormattingContext}
                    />
                  );
                })}
              </Box>
              
              {/* Display definitions for selected options with definitions */}
              {question.options
                .filter(option => 
                  (selectedAnswers[qIdx] || []).includes(option.label) && 
                  visibleDefinitions.includes(option.label) &&
                  option.definition
                )
                .map(option => (
                  <Card 
                    key={`def-${option.label}`} 
                    sx={{ 
                      mt: 1.5, 
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}>
                            {option.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                            {option.definition}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleCloseDefinition(option.label)}
                          sx={{ mt: -0.5, mr: -0.5 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              }
            </Box>
          ))}

          {/* Apply Button */}
          <Button
            variant="contained"
            onClick={handleApplyAnswers}
            disabled={isFormattingContext}
            startIcon={isFormattingContext ? <CircularProgress size={16} /> : <AutoAwesome />}
            fullWidth
          >
            {isFormattingContext ? 'Applying...' : 'Apply Answers to Description'}
          </Button>
        </Paper>
      </Collapse>}
    </Box>
  );
};

export default ContextAssistant;

